import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { Donut, TimeSeries } from "@aurumiq/ui/composite/charts";
import { KpiTile } from "@aurumiq/ui/composite/kpi-tile";
import { int } from "@aurumiq/ui/lib/format";
import { useState } from "react";
import { PageHeader } from "@/app/page-header";
import { NativeSelect } from "@/components/field";
import { QueryState } from "@/components/query-state";
import { CrashGroupsTable } from "@/features/telemetry/crash-groups-table";
import { useTelemetryOverview } from "@/features/telemetry/queries";
import type { TelemetryOverview } from "@/lib/types";

function Loaded({ t, days }: { t: TelemetryOverview; days: number }) {
  const daily = t.daily.map((d) => ({ ...d, label: d.day.slice(5) }));
  const totalVersions = t.versions.reduce((s, v) => s + v.devices, 0);
  const totalPlatforms = t.platforms.reduce((s, v) => s + v.devices, 0);
  return (
    <div className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <KpiTile label="Active devices" value={int(t.totals.devices_active)} />
        <KpiTile label="DAU" value={int(t.totals.dau)} hint="devices seen today" />
        <KpiTile label="WAU" value={int(t.totals.wau)} hint="devices seen this week" />
        <KpiTile label={`Invoices (${days} d)`} value={int(t.totals.invoices_30d)} />
        <KpiTile label={`Verify scans (${days} d)`} value={int(t.totals.scans_30d)} />
        <KpiTile
          label={`Crashes (${days} d)`}
          value={int(t.totals.crashes_30d)}
          className={t.totals.crashes_30d > 0 ? "border-warning/50" : undefined}
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Active devices</CardTitle>
          </CardHeader>
          <CardContent>
            <TimeSeries
              title="Active devices"
              data={daily}
              xKey="label"
              kind="area"
              series={[
                { key: "active_devices", label: "Devices" },
                { key: "sessions", label: "Sessions" },
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            <TimeSeries
              title="Invoices"
              data={daily}
              xKey="label"
              series={[
                { key: "invoices_issued", label: "Issued" },
                { key: "invoices_voided", label: "Voided" },
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Crashes &amp; scans</CardTitle>
          </CardHeader>
          <CardContent>
            <TimeSeries
              title="Crashes & scans"
              data={daily}
              xKey="label"
              series={[
                { key: "crashes", label: "Crashes" },
                { key: "scans", label: "Scans" },
              ]}
            />
          </CardContent>
        </Card>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>App versions</CardTitle>
          </CardHeader>
          <CardContent>
            <Donut
              title="App versions"
              data={t.versions.map((v) => ({ name: v.app_version, value: v.devices }))}
              center={int(totalVersions)}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Platforms</CardTitle>
          </CardHeader>
          <CardContent>
            <Donut
              title="Platforms"
              data={t.platforms.map((p) => ({ name: p.platform, value: p.devices }))}
              center={int(totalPlatforms)}
            />
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Crash groups</CardTitle>
        </CardHeader>
        <CardContent>
          <CrashGroupsTable groups={t.crash_groups} />
        </CardContent>
      </Card>
    </div>
  );
}

export function TelemetryPage() {
  const [days, setDays] = useState(30);
  const q = useTelemetryOverview(days);
  return (
    <>
      <PageHeader
        title="Telemetry"
        description="Fleet-wide usage from desktop check-ins; per-organisation detail lives on each org."
        actions={
          <NativeSelect
            aria-label="Period"
            className="w-32"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </NativeSelect>
        }
      />
      <QueryState query={q} rows={6}>
        {(t) => <Loaded t={t} days={days} />}
      </QueryState>
    </>
  );
}
